import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Loader2, FileImage, FileText, Layers, Merge, Replace, AlertTriangle } from "lucide-react";
import { uploadDesignSource } from "../lib/api";

/**
 * UploadBlueprintDialog
 *
 * Dropzone + preview flow that lets the user upload a floor-plan file
 * (image, PDF, or CAD) and — for vision-parsable formats — see a preview
 * of the AI-parsed blueprint before choosing to REPLACE or MERGE it into
 * the current design.
 *
 * Props:
 *   open            — bool, controls visibility
 *   designId        — id of the design to attach the upload to
 *   currentRoomCount — number, used in the "current vs parsed" preview
 *   currentFloorCount — number of floors already in the design (for the
 *                      floor selector; defaults to 1)
 *   onClose         — () => void
 *   onApply         — ({ blueprint, mode: 'replace' | 'merge' }) => Promise<void>
 *                     (parent is responsible for actually saving the new bp)
 */
export default function UploadBlueprintDialog({
  open,
  designId,
  currentRoomCount = 0,
  currentFloorCount = 1,
  onClose,
  onApply,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null); // { source, parsed_blueprint, summary, room_count, cad_stored_only }
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0); // V2.0.91 — seconds since upload started (heartbeat-driven)
  // V2.0.91 — Which floor the uploaded file represents. `-1` means
  // "let the AI decide" (default; multi-page PDFs may add extra floors).
  // 0/1/2… tags every parsed room with that floor_index.
  const [floorIndex, setFloorIndex] = useState(-1);
  const inputRef = useRef(null);
  const startRef = useRef(0);

  const floorCount = Math.max(1, currentFloorCount || 1);

  const reset = () => {
    setPreview(null);
    setError("");
    setUploading(false);
    setApplying(false);
    setDragOver(false);
    setElapsed(0);
    setFloorIndex(-1);
  };

  const handleClose = useCallback(() => {
    if (uploading || applying) return;
    reset();
    onClose?.();
  }, [uploading, applying, onClose]);

  const handleFile = useCallback(async (file) => {
    if (!file || !designId) return;
    setError("");
    setPreview(null);
    setUploading(true);
    setElapsed(0);
    startRef.current = Date.now();
    try {
      const r = await uploadDesignSource(designId, file, {
        floorIndex: floorIndex >= 0 ? floorIndex : undefined,
        onHeartbeat: () => {
          const secs = Math.max(0, Math.floor((Date.now() - startRef.current) / 1000));
          setElapsed(secs);
        },
      });
      setPreview(r.data);
      if (r.data?.cad_stored_only) {
        toast(r.data.summary || "CAD file stored.");
      } else if ((r.data?.room_count ?? 0) === 0) {
        toast("The AI couldn't identify any rooms in this file — try a clearer plan.");
      } else {
        toast.success(`Parsed ${r.data.room_count} room${r.data.room_count === 1 ? "" : "s"} from your file`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      setError(detail);
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }, [designId, floorIndex]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const apply = async (mode) => {
    if (!preview?.parsed_blueprint) return;
    setApplying(true);
    try {
      await onApply?.({ blueprint: preview.parsed_blueprint, mode });
      toast.success(mode === "replace" ? "Design replaced from your file" : "Parsed rooms merged into your design");
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  // V2.0.91 — the floor selector shows an option for every existing
  // floor plus one "Add a new floor" slot at the end so multi-storey
  // buildings can be assembled one file at a time.
  const floorOptions = [];
  for (let i = 0; i < floorCount; i++) floorOptions.push({ value: i, label: `Floor ${i + 1}` });
  floorOptions.push({ value: floorCount, label: `+ Add new floor (Floor ${floorCount + 1})` });

  return (
    <div
      data-testid="upload-blueprint-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-zinc-950/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="w-full max-w-xl bg-zinc-950 border border-brass-500/40 shadow-[0_12px_64px_rgba(196,169,107,0.15)]">
        <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-1">
              // Upload Floor Plan
            </div>
            <h2 className="font-display text-xl text-zinc-50">Start from an image, PDF, or CAD file</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-zinc-400 hover:text-zinc-100 p-1 disabled:opacity-40"
            disabled={uploading || applying}
            aria-label="Close upload dialog"
            data-testid="upload-blueprint-close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="p-6 space-y-5">
          {!preview && !uploading && (
            <div className="border border-zinc-800 bg-zinc-900/40 p-3" data-testid="upload-floor-picker">
              <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-2">
                // Assign to floor
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setFloorIndex(-1)}
                  data-testid="upload-floor-auto"
                  className={
                    "font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition " +
                    (floorIndex === -1
                      ? "border-brass-400 text-brass-200 bg-brass-950/40"
                      : "border-zinc-700 text-zinc-400 hover:text-zinc-100")
                  }
                >
                  Auto-detect
                </button>
                {floorOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFloorIndex(opt.value)}
                    data-testid={`upload-floor-${opt.value}`}
                    className={
                      "font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition " +
                      (floorIndex === opt.value
                        ? "border-brass-400 text-brass-200 bg-brass-950/40"
                        : "border-zinc-700 text-zinc-400 hover:text-zinc-100")
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="font-body text-[11px] text-zinc-500 mt-2 leading-relaxed">
                {floorIndex === -1
                  ? "The AI decides the floor layout. Multi-page PDFs may parse as multiple storeys."
                  : "Every room parsed from this file will be placed on the selected floor. Upload one file per storey to build a multi-floor plan."}
              </div>
            </div>
          )}

          {!preview && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => !uploading && inputRef.current?.click()}
              data-testid="upload-blueprint-dropzone"
              className={
                "cursor-pointer border-2 border-dashed p-8 text-center transition-colors " +
                (dragOver
                  ? "border-brass-400 bg-brass-950/30"
                  : "border-zinc-800 hover:border-brass-500/60 bg-zinc-900/40")
              }
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.dwg,.dxf"
                onChange={onPick}
                className="hidden"
                data-testid="upload-blueprint-file-input"
                disabled={uploading}
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-3 py-4" data-testid="upload-blueprint-progress">
                  <Loader2 className="w-8 h-8 text-brass-400 animate-spin" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400">
                    Analysing floor plan…
                  </div>
                  <div className="font-body text-xs text-zinc-500 max-w-xs text-center">
                    Gemini is reading the geometry. Complex PDFs can take up to 90 seconds — the connection is kept alive with heartbeats.
                  </div>
                  {elapsed > 0 && (
                    <div className="font-mono text-[10px] text-zinc-500" data-testid="upload-blueprint-elapsed">
                      {elapsed}s elapsed
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 grid place-items-center border border-brass-500/50 bg-brass-950/20">
                    <Upload className="w-6 h-6 text-brass-400" />
                  </div>
                  <div className="font-display text-lg text-zinc-100">Drop your floor plan here</div>
                  <div className="font-body text-xs text-zinc-500 max-w-sm">
                    Sketch, photo, architectural PDF, or CAD file (DWG/DXF).
                    <br />
                    The AI will turn it into an editable blueprint automatically.
                  </div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                    <Badge icon={FileImage} label="PNG · JPG · WEBP" />
                    <Badge icon={FileText} label="PDF" />
                    <Badge icon={Layers} label="DWG · DXF" muted />
                  </div>
                  <div className="font-mono text-[9px] text-zinc-500 mt-2">Max 15 MB · Free for all users</div>
                </div>
              )}
            </div>
          )}

          {error && !uploading && (
            <div className="flex items-start gap-2 border border-rose-800/60 bg-rose-950/40 text-rose-200 px-4 py-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="font-mono text-[11px] leading-relaxed">{error}</div>
            </div>
          )}

          {preview && (
            <div className="space-y-4" data-testid="upload-blueprint-preview">
              <div className="border border-brass-500/40 bg-brass-950/20 px-4 py-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-1">
                  // File Uploaded
                </div>
                <div className="font-body text-sm text-zinc-100 truncate">
                  {preview.source?.filename || "upload"}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                  {(preview.source?.mime || "unknown")} · {formatBytes(preview.source?.size || 0)}
                </div>
              </div>

              {preview.cad_stored_only ? (
                <div className="border border-amber-700/50 bg-amber-950/20 px-4 py-4 space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
                    // CAD Auto-Parse Coming Soon
                  </div>
                  <div className="font-body text-sm text-zinc-200 leading-relaxed">
                    {preview.summary}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-zinc-800 p-3">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                        Current design
                      </div>
                      <div className="font-display text-2xl text-zinc-100">
                        {currentRoomCount} <span className="text-sm text-zinc-500">rooms</span>
                      </div>
                    </div>
                    <div className="border border-brass-500/50 p-3 bg-brass-950/20">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-1">
                        Parsed from your file
                      </div>
                      <div className="font-display text-2xl text-brass-100">
                        {preview.room_count} <span className="text-sm text-brass-400/60">rooms</span>
                      </div>
                    </div>
                  </div>
                  {preview.summary && (
                    <div className="font-body text-xs text-zinc-400 leading-relaxed border-l-2 border-brass-500/40 pl-3">
                      {preview.summary}
                    </div>
                  )}
                  {preview.room_count === 0 && (
                    <div className="flex items-start gap-2 border border-amber-800/60 bg-amber-950/30 text-amber-200 px-4 py-3">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <div className="font-mono text-[11px] leading-relaxed">
                        No rooms were detected. Try a clearer image (top-down view, labelled rooms) or start from the chat assistant instead.
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-6 py-4 border-t border-zinc-800 bg-zinc-900/40 flex-wrap">
          {preview && !preview.cad_stored_only && preview.room_count > 0 && (
            <>
              <button
                type="button"
                onClick={reset}
                disabled={applying}
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100 px-4 py-2 disabled:opacity-40"
                data-testid="upload-blueprint-reset"
              >
                Try another file
              </button>
              <button
                type="button"
                onClick={() => apply("merge")}
                disabled={applying}
                data-testid="upload-blueprint-merge"
                title="Add parsed rooms to your existing design"
                className="inline-flex items-center gap-1.5 border border-brass-700/60 bg-brass-950/20 hover:bg-brass-900/40 text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-40"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Merge className="w-3.5 h-3.5" />}
                Merge into design
              </button>
              <button
                type="button"
                onClick={() => apply("replace")}
                disabled={applying}
                data-testid="upload-blueprint-replace"
                title="Wipe the current design and start fresh from the parsed file"
                className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white font-mono text-[10px] uppercase tracking-widest px-4 py-2 disabled:opacity-50"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Replace className="w-3.5 h-3.5" />}
                Replace design
              </button>
            </>
          )}
          {(!preview || preview.cad_stored_only || preview.room_count === 0) && (
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-zinc-100 px-4 py-2 disabled:opacity-40"
              data-testid="upload-blueprint-cancel"
            >
              {preview ? "Done" : "Cancel"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function Badge({ icon: Icon, label, muted = false }) {
  return (
    <span className={
      "inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-2 py-1 border " +
      (muted
        ? "border-zinc-800 text-zinc-500 bg-zinc-900/40"
        : "border-brass-500/40 text-brass-300 bg-brass-950/20")
    }>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}
